import { useRef, useState } from "react";
import { useAuthContext } from "../contexts/auth-context";
import { apiRequestSecuredDelete, apiRequestSecuredPost } from "../lib/common/api";
import { TProcessStates } from "../lib/types/general";

type TFileNameChangeHandler = (newfile?: string, inappropriateContent?: boolean, unsupportedImageType?: boolean, errorMessage?: string) => void;
type TUploadType = "image" | "resume";
type TUploadPurpose = "fixed" | "gallery";
type TUploadErrorFlags = {
    inappropriateContent: boolean;
    unsupportedImageType: boolean;
    errorMessage?: string;
};
type TResumeViewerLinkResponse = {
    data: {
        secureUrl: string;
    };
};

const ALLOWED_IMAGE_MIME_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence"]);
const HEIC_MIME_TYPES = new Set(["image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence"]);
const HEIC_FILE_EXTENSIONS = new Set(["heic", "heif"]);
const ALLOWED_IMAGE_INPUT_ACCEPT = "image/jpeg,image/png,image/jpg,image/heic,image/heif,.heic,.heif";

const getFileExtension = (fileName: string) => {
    const lastDotIndex = fileName.lastIndexOf(".");
    if (lastDotIndex === -1) {
        return "";
    }

    return fileName.slice(lastDotIndex + 1).toLowerCase();
};

const isHeicFileVerifier = (file: File) => {
    return HEIC_MIME_TYPES.has(file.type) || HEIC_FILE_EXTENSIONS.has(getFileExtension(file.name));
};

const isAllowedImageFile = (file: File) => {
    return ALLOWED_IMAGE_MIME_TYPES.has(file.type) || isHeicFileVerifier(file);
};

export const useFileManagement = () => {
    const { getUserTokenId, userInfo } = useAuthContext();

    const galleryUploadInProgress = useRef(false);
    const [state, setState] = useState<TProcessStates>("idle");

    const getUploadErrorFlags = (error: any): TUploadErrorFlags => {
        const errorData = error?.response?.data;
        const errorMessage = error?.message || "";

        if (errorData?.inappropriateContent) {
            return { inappropriateContent: true, unsupportedImageType: false };
        }

        if (errorMessage === "Files can't be bigger than 10 MB") {
            return { inappropriateContent: false, unsupportedImageType: false, errorMessage };
        }

        if (
            errorData?.unsupportedImageType ||
            errorMessage === "File should be an image" ||
            errorMessage === "Please upload a JPG, JPEG, PNG, HEIC, or HEIF image." ||
            errorMessage === "File should be an document PDF or DOC"
        ) {
            return { inappropriateContent: false, unsupportedImageType: true };
        }

        return { inappropriateContent: false, unsupportedImageType: false };
    };

    const getImagePath = (image: string, purpose: TUploadPurpose, userGUID?: string) => {
        return `${process.env.REACT_APP_BUCKETEER_URL}${userGUID || userInfo?.guid}/images/${purpose === "gallery" ? "gallery/" : ""}${image}`;
    };

    /**
     * this is used for uploading fixed data like resume, logo, background, etc.
     * @param fileName file name to assign to the file
     * @param fileContent content to upload
     * @returns
     */
    const uploadFixed = async (
        fileName: string,
        fileContent: string,
        fileType: TUploadType,
    ): Promise<{
        fileUrl: string;
        inappropriateContent?: boolean;
        unsupportedImageType?: boolean;
    }> => {
        setState("working");
        try {
            const imageResponse = await apiRequestSecuredPost<{
                fileUrl: string;
                inappropriateContent?: boolean;
                unsupportedImageType?: boolean;
            }>("files/upload", await getUserTokenId(), {
                fileName,
                fileContent,
                fileType,
            });

            const inappropriateContent = imageResponse.data?.inappropriateContent ?? false;
            const unsupportedImageType = imageResponse.data?.unsupportedImageType ?? false;

            setState("idle");
            return {
                fileUrl: fileName,
                inappropriateContent,
                unsupportedImageType,
            };
        } catch (error: any) {
            if (error.response?.data?.inappropriateContent) {
                setState("idle");
                return { fileUrl: "", inappropriateContent: true };
            }
            if (error.response?.data?.unsupportedImageType) {
                setState("idle");
                return { fileUrl: "", unsupportedImageType: true };
            } else {
                setState("idle");
                throw error;
            }
        }
    };
    /**
     * this is used for uploading images to a gallery
     * @param fileName file name to assign to the file
     * @param fileContent content to upload
     * @returns
     */
    const uploadToGallery = async (
        fileName: string,
        fileContent: string,
    ): Promise<{
        fileUrl: string;
        inappropriateContent?: boolean;
        unsupportedImageType?: boolean;
    }> => {
        try {
            const imageResponse = await apiRequestSecuredPost<{
                fileUrl: string;
                inappropriateContent?: boolean;
                unsupportedImageType?: boolean;
            }>("files/uploadgallery", await getUserTokenId(), {
                fileName,
                fileContent,
            });
            const inappropriateContent = imageResponse.data?.inappropriateContent ?? false;
            const unsupportedImageType = imageResponse.data?.unsupportedImageType ?? false;

            return {
                fileUrl: fileName,
                inappropriateContent,
                unsupportedImageType,
            };
        } catch (error: any) {
            if (error.response?.data?.inappropriateContent) {
                return { fileUrl: "", inappropriateContent: true };
            } else if (error.response?.data?.unsupportedImageType) {
                return { fileUrl: "", unsupportedImageType: true };
            } else {
                throw error;
            }
        }
    };

    /**
     * used for uploading fixed images/documents like logo, background, resume
     * @param file
     * @param fileName
     * @param fileNameChangeHandler
     * @returns
     */
    const controledFileUploadFixed = (
        file: any,
        fileName: string,
        fileType: TUploadType,
        fileNameChangeHandler?: TFileNameChangeHandler,
    ): Promise<{
        fileUrl: string;
        inappropriateContent?: boolean;
        unsupportedImageType?: boolean;
    }> => {
        const promise = new Promise<{
            fileUrl: string;
            inappropriateContent?: boolean;
            unsupportedImageType?: boolean;
        }>((resolve, reject) => {
            fileNameChangeHandler && fileNameChangeHandler(undefined);

            let reader = new FileReader();
            reader.onload = async () => {
                let fileInfo = {
                    fileContent: (reader.result as string)?.split(",")[1],
                    fileName: file.name,
                };
                try {
                    const { fileUrl, inappropriateContent, unsupportedImageType } = await uploadFixed(fileName, fileInfo.fileContent, fileType);

                    if (inappropriateContent) {
                        resolve({ fileUrl, inappropriateContent: true });
                    } else if (unsupportedImageType) {
                        resolve({ fileUrl, unsupportedImageType: true });
                    } else {
                        fileNameChangeHandler && fileNameChangeHandler(fileUrl);
                        resolve({
                            fileUrl,
                            inappropriateContent: false,
                            unsupportedImageType: false,
                        });
                    }
                } catch (error) {
                    console.error("Error occurred during file upload:", error);
                    reject(error);
                }
            };
            reader.readAsDataURL(file);
        });

        return promise;
    };
    /**
     * used for uploading images to gallery
     * @param file
     * @param fileName
     * @param fileNameChangeHandler
     * @returns
     */
    const controledFileUploadGallery = (
        file: any,
        fileName: string,
        fileNameChangeHandler?: TFileNameChangeHandler,
    ): Promise<{
        fileUrl: string;
        inappropriateContent?: boolean;
        unsupportedImageType?: boolean;
    }> => {
        const promise = new Promise<{
            fileUrl: string;
            inappropriateContent?: boolean;
            unsupportedImageType?: boolean;
        }>((resolve, reject) => {
            fileNameChangeHandler && fileNameChangeHandler(undefined);

            let reader = new FileReader();
            reader.onload = async () => {
                let fileInfo = {
                    fileContent: (reader.result as string)?.split(",")[1],
                    fileName: file.name,
                };
                try {
                    const { fileUrl, inappropriateContent, unsupportedImageType } = await uploadToGallery(fileName, fileInfo.fileContent);

                    if (inappropriateContent) {
                        resolve({ fileUrl, inappropriateContent: true });
                    } else if (unsupportedImageType) {
                        resolve({ fileUrl, unsupportedImageType: true });
                    } else {
                        fileNameChangeHandler && fileNameChangeHandler(fileUrl);
                        resolve({
                            fileUrl,
                            inappropriateContent: false,
                            unsupportedImageType: false,
                        });
                    }
                } catch (error: any) {
                    console.error("Error occurred during file upload:", error);
                    reject(error);
                }
            };
            reader.onerror = () => reject(new Error("Unable to read image"));
            reader.onabort = () => reject(new Error("Image reading interrupted"));
            reader.readAsDataURL(file);
        });

        return promise;
    };

    /**
     *
     * @param file
     * @param fileName
     * @param fileType
     * @param fileNameChangeHandler
     * @returns
     */
    const imageFileChecker = async (
        file: any,
        fileName: string,
        fileType: TUploadType,
        purpose: TUploadPurpose,
    ): Promise<{
        fileUrl: string;
        inappropriateContent?: boolean;
        unsupportedImageType?: boolean;
        errorMessage?: string;
    }> => {
        try {
            if (file.size > 10 * 1024 * 1024) {
                throw new Error("Files can't be bigger than 10 MB");
            }
            if (!isAllowedImageFile(file)) {
                throw new Error("Please upload a JPG, JPEG, PNG, HEIC, or HEIF image.");
            }

            switch (purpose) {
                case "fixed":
                    const response = await controledFileUploadFixed(file, fileName, fileType);
                    return response;

                case "gallery":
                    const galleryResponse = await controledFileUploadGallery(file, fileName);
                    return galleryResponse;
                default:
                    throw new Error("Invalid upload purpose");
            }
        } catch (error) {
            console.error("Error occurred during image file checking:", error);
            const { inappropriateContent, unsupportedImageType, errorMessage } = getUploadErrorFlags(error);
            return {
                fileUrl: "",
                inappropriateContent,
                unsupportedImageType,
                errorMessage,
            };
        }
    };

    /**
     *
     * @param file
     * @param fileName
     * @param fileType
     * @param fileNameChangeHandler
     * @returns
     */
    const documentFileChecker = async (file: any, fileName: string, fileType: TUploadType, fileNameChangeHandler?: TFileNameChangeHandler) => {
        if (file.size > 10 * 1024 * 1024) {
            throw new Error("Files can't be bigger than 10 MB");

            //alert("Los archivos no puede tener más de 10 MB"); //TODO: i18n
            //return;
        }
        if (
            file.type.indexOf("application/pdf") === -1 &&
            file.type.indexOf("application/msword") === -1 && //(.doc)
            file.type.indexOf(
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document", //(.docx)
            ) === -1
        ) {
            throw new Error("Please upload a PDF, DOC, or DOCX file.");
        }

        const documentFileNameParts: string[] = file.name.split(".");
        const documentfileExtension = documentFileNameParts[documentFileNameParts.length - 1];
        return controledFileUploadFixed(file, `${fileName}.${documentfileExtension}`, fileType, fileNameChangeHandler);
    };

    const clearDragAndDropEvents = (component: HTMLElement) => {
        component.ondragover = null;
        component.ondragstart = null;
        component.ondragleave = null;
        component.ondrag = null;
        component.ondragend = null;
        component.ondrop = null;
        component.onmousemove = null;
        component.onmousedown = null;
        component.onclick = null;
        component.onpointerdown = null;
        component.onpointermove = null;
        component.onpointerup = null;
        component.onpointercancel = null;
        component.style.touchAction = "";
    };

    //Spinner loader section starts

    const uploadLoaderIconClass = "upload-spinner-overlay__icon";
    const componentLoaderClassAttributeName = "data-component-loader-class";

    const createSpinnerLoaderClass = () => `${uploadLoaderIconClass}--${crypto.randomUUID()}`;

    const startImageUploadLoader = (component: HTMLElement) => {
        const currentLoaderClass = component.getAttribute(componentLoaderClassAttributeName);
        if (currentLoaderClass) {
            component.querySelector(`.${currentLoaderClass}`)?.remove();
        }

        component.classList.add("upload-spinner-overlay");
        component.setAttribute("aria-busy", "true");

        const spinnerWrapper = document.createElement("div");
        const spinnerIcon = document.createElement("span");
        const spinnerLoaderClass = createSpinnerLoaderClass();

        spinnerWrapper.className = `${uploadLoaderIconClass} ${spinnerLoaderClass}`;
        spinnerWrapper.setAttribute("aria-hidden", "true");
        spinnerIcon.className = "fa-solid fa-spinner fa-spin text-white";
        spinnerWrapper.appendChild(spinnerIcon);
        component.setAttribute(componentLoaderClassAttributeName, spinnerLoaderClass);
        component.appendChild(spinnerWrapper);
    };

    const stopImageUploadLoader = (component: HTMLElement) => {
        component.classList.remove("upload-spinner-overlay");
        component.removeAttribute("aria-busy");

        const currentLoaderClass = component.getAttribute(componentLoaderClassAttributeName);
        if (currentLoaderClass) {
            component.querySelector(`.${currentLoaderClass}`)?.remove();
        }
        component.removeAttribute(componentLoaderClassAttributeName);
    };

    //Spinner loader section ends

    const makeDraggableCapableForImageResize = (htmlElement: React.RefObject<HTMLElement>, moveHandler: (x: number, y: number) => void) => {
        if (!htmlElement.current) {
            setTimeout(() => {
                makeDraggableCapableForImageResize(htmlElement, moveHandler);
            }, 1000);
            return;
        }
        const component = htmlElement.current;
        clearDragAndDropEvents(component);
        component.style.touchAction = "none";

        let activePointerId: number | null = null;
        let lastX = 0;
        let lastY = 0;

        component.onpointerdown = (e: PointerEvent) => {
            e.preventDefault();
            e.stopPropagation();
            activePointerId = e.pointerId;
            lastX = e.clientX;
            lastY = e.clientY;
            component.setPointerCapture?.(e.pointerId);
        };
        component.onpointermove = (e: PointerEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (activePointerId !== e.pointerId) {
                return;
            }

            const movementX = e.clientX - lastX;
            const movementY = e.clientY - lastY;
            lastX = e.clientX;
            lastY = e.clientY;
            moveHandler(movementX, movementY);
        };
        component.onpointerup = (e: PointerEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (activePointerId !== e.pointerId) {
                return;
            }

            component.releasePointerCapture?.(e.pointerId);
            activePointerId = null;
        };
        component.onpointercancel = (e: PointerEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (activePointerId !== e.pointerId) {
                return;
            }

            component.releasePointerCapture?.(e.pointerId);
            activePointerId = null;
        };
        component.onclick = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
        };
    };

    /**
     *
     * @param htmlElement
     * @returns
     */
    const makeDroppableCapable = (
        htmlElement: React.RefObject<HTMLElement>,
        fileNameToAssign: string,
        uploadType: TUploadType,
        uploadPurpose: TUploadPurpose,
        fileNameChangeHandler?: TFileNameChangeHandler,
        maxFiles?: number,
    ): Promise<{
        fileUrl: string;
        inappropriateContent?: boolean;
        unsupportedImageType?: boolean;
        errorMessage?: string;
    }> => {
        if (!htmlElement.current) {
            // GallerySection configures uploads when its conditional drop zone mounts.
            // Retrying here could overwrite that configuration with an outdated capacity.
            if (uploadPurpose !== "gallery") {
                setTimeout(() => {
                    makeDroppableCapable(htmlElement, fileNameToAssign, uploadType, uploadPurpose, fileNameChangeHandler, maxFiles);
                }, 1000);
            }
            return Promise.resolve({
                fileUrl: "",
                inappropriateContent: false,
                unsupportedImageType: false,
            });
        }

        const component = htmlElement.current;
        clearDragAndDropEvents(component);

        component.ondragover = (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            component.classList.add("dropable-component");
        };

        component.ondragleave = (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            component.classList.remove("dropable-component");
        };
        component.ondrop = async (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            component.classList.remove("dropable-component");

            if (uploadPurpose === "gallery" && uploadType === "image") {
                const files = Array.from(e.dataTransfer?.files ?? []);
                if (!files.length || galleryUploadInProgress.current) return;
                galleryUploadInProgress.current = true;
                setState("working");
                startImageUploadLoader(component);
                let uploadedFile: string | undefined;
                const errors: string[] = [];
                try {
                    if (maxFiles !== undefined && files.length > maxFiles) {
                        await fileNameChangeHandler?.(
                            undefined,
                            false,
                            false,
                            `You can only add ${maxFiles} more ${maxFiles === 1 ? "image" : "images"} to this gallery. Please select fewer images.`,
                        );
                        return;
                    }
                    for (const file of files) {
                        const response = await imageFileChecker(file, file.name, uploadType, uploadPurpose);
                        if (response.errorMessage || response.inappropriateContent || response.unsupportedImageType || !response.fileUrl) {
                            let message = response.errorMessage || "Upload failed. Please try again.";
                            if (response.inappropriateContent) message = "Inappropriate content detected.";
                            if (response.unsupportedImageType) message = "Please upload a JPG, JPEG, PNG, HEIC, or HEIF image.";
                            errors.push(`${file.name}: ${message}`);
                        } else {
                            uploadedFile = response.fileUrl;
                        }
                    }
                    await fileNameChangeHandler?.(uploadedFile, false, false, errors.join(" "));
                } finally {
                    galleryUploadInProgress.current = false;
                    setState("idle");
                    stopImageUploadLoader(component);
                }
                return;
            }

            startImageUploadLoader(component);

            try {
                switch (uploadType) {
                    case "resume":
                        const resumeResponse = await documentFileChecker(e.dataTransfer?.files[0], fileNameToAssign, uploadType, fileNameChangeHandler);

                        stopImageUploadLoader(component);
                        return resumeResponse.fileUrl;

                    case "image":
                        const { fileUrl, inappropriateContent, unsupportedImageType, errorMessage } = await imageFileChecker(
                            e.dataTransfer?.files[0],
                            fileNameToAssign,
                            uploadType,
                            uploadPurpose,
                        );

                        if (errorMessage) {
                            fileNameChangeHandler && fileNameChangeHandler(undefined, false, false, errorMessage);
                        } else if (inappropriateContent) {
                            fileNameChangeHandler && fileNameChangeHandler(fileUrl, true);
                        } else if (unsupportedImageType) {
                            fileNameChangeHandler && fileNameChangeHandler(fileUrl, false, true);
                        } else {
                            fileNameChangeHandler && fileNameChangeHandler(fileUrl, false);
                        }

                        stopImageUploadLoader(component);
                        return Promise.resolve({
                            fileUrl: "",
                            inappropriateContent: false,
                            unsupportedImageType: false,
                        });
                    default:
                        throw new Error("upload type incorrect");
                }
            } catch (error: any) {
                console.error("Error occurred:", error);
                if (uploadType === "resume") {
                    fileNameChangeHandler && fileNameChangeHandler("", false, false, error.message || "Resume upload failed");
                } else {
                    const { errorMessage } = getUploadErrorFlags(error);
                    if (fileNameChangeHandler && errorMessage) {
                        fileNameChangeHandler(undefined, false, false, errorMessage);
                    }
                }
                stopImageUploadLoader(component);
                const { inappropriateContent, unsupportedImageType } = getUploadErrorFlags(error);
                if (fileNameChangeHandler && (inappropriateContent || unsupportedImageType)) {
                    fileNameChangeHandler("", inappropriateContent, unsupportedImageType);
                }
                return Promise.resolve({
                    fileUrl: "",
                    inappropriateContent,
                    unsupportedImageType,
                });
            }
        };

        return Promise.resolve({
            fileUrl: "",
            inappropriateContent: false,
            unsupportedImageType: false,
        });
    };

    const deleteResumeApi = async (userId: number): Promise<any> => {
        return apiRequestSecuredDelete("files/delete/resume", await getUserTokenId(), {
            userId: userId,
        });
    };

    const getResumeViewerUrl = async (candidateUserId: number): Promise<string> => {
        const response = await apiRequestSecuredPost<TResumeViewerLinkResponse>("files/resume/view-link", await getUserTokenId(), {
            candidateUserId,
        });

        return response.data.data.secureUrl;
    };

    return {
        state,
        makeDraggableCapableForImageResize,
        makeDroppableCapable,
        getImagePath,
        imageFileChecker,
        deleteResumeApi,
        getResumeViewerUrl,
    };
};

type TFileUploadComponentProps = {
    relatedComponent: React.RefObject<HTMLElement>;
    fileToolTipText: string;
    tooltipPosition: "left" | "right" | "down" | "downRight";
    accept?: string;
    multiple?: boolean;
};

export const FileUploaderComponent = ({
    relatedComponent,
    fileToolTipText,
    tooltipPosition,
    accept = ALLOWED_IMAGE_INPUT_ACCEPT,
    multiple = false,
}: TFileUploadComponentProps) => {
    const fileChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (relatedComponent.current) {
            const htmlElement = relatedComponent.current;
            if (htmlElement.ondrop) {
                htmlElement.ondrop({
                    preventDefault: e.preventDefault,
                    stopPropagation: e.stopPropagation,
                    dataTransfer: {
                        files: e.target.files,
                    },
                } as DragEvent);
            }
        }
        e.target.value = "";
    };

    const fileRef = useRef<HTMLInputElement>(null);
    const tooltipClass = `${tooltipPosition}`;

    return (
        <>
            <div className="d-flex flex-row-reverse">
                {fileToolTipText && (
                    <button className={`btn bg-white fileTooltip ${tooltipClass} `} data-tooltip={fileToolTipText}>
                        <i
                            className="fa-solid fa-camera fa-md text-center shadow editIcon"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (fileRef.current) {
                                    fileRef.current.click();
                                }
                            }}
                        ></i>
                    </button>
                )}
                {!fileToolTipText && (
                    <button
                        className="btn btn-outline-primary w-100"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (fileRef.current) {
                                fileRef.current.click();
                            }
                        }}
                    >
                        Click to Upload
                    </button>
                )}
            </div>
            <input type="file" ref={fileRef} onChange={fileChangeHandler} className="d-none" accept={accept} multiple={multiple}></input>
        </>
    );
};
